// The open message of the INBOX (docs/design/inbox.md §6; round 089
// cards 05 a to 13 b, and 24 b for the page). A message opens as a page
// of the INBOX with ‹ INBOX at its head, on both forms: the head with
// the bucket icon, the type word, the bucket tag and the stamp over the
// subject; the figure strip at the next type step; the sentences; the
// table or the figure of its type; the place line with GO TO; the
// presses; and the date the INBOX deletes it (audit P8). A fold opens to
// the table of its members.

import type { InboxMsg } from '../../gen/proto/aquaphobia';
import { inbox as S } from '../../gen/strings';
import { isoDate, isoDateTime, isoTime } from '../../locale/stamp';
import type { GameState } from '../../state';
import { esc } from '../esc';
import { inboxIcon } from './icons';
import { bucketIcon, bucketOf, bucketTag, cellHtml, goToPress, memberLines, pressesHtml } from './rows';
import {
  type OpenTable, bodyOf, cellLabel, cellsOf, factsOf, figureOf, placeOf, subjectOf, tableOf, typeWord,
} from './words';

/** The stamp of the head: the date and the time; a fold reads its range. */
function headStamp(first: number, last: number): string {
  if (first === last || first <= 0) return isoDateTime(last);
  const end = isoDate(first) === isoDate(last) ? isoTime(last) : isoDateTime(last);
  return S.range({ first: isoDateTime(first), last: end });
}

/** A table of 22 px lines in tabular figures; a figure column reads to
 *  the right. It scrolls inside its own box, so a wide table never moves
 *  the page sideways. */
function tableHtml(t: OpenTable): string {
  const head = t.heads.map((h, i) => `<th${t.num[i] ? ' class="r"' : ''}>${esc(h)}</th>`).join('');
  const rows = t.rows.map((r) => `<tr>${r.map((c, i) => `<td${t.num[i] ? ' class="r"' : ''}>${c}</td>`).join('')}</tr>`).join('');
  return `<div class="ib-tw">${t.title ? `<div class="ib-tt">${esc(t.title)}</div>` : ''}`
    + `<table class="ib-tbl"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table></div>`;
}

/** The page of one message or one fold. */
export function openHtml(m: InboxMsg, s: GameState): string {
  const f = factsOf(m, s);
  const b = bucketOf(m);
  const word = typeWord(f);
  const kind = [word ? `${word}${f.count ? ` ×${f.count}` : ''}` : '', bucketTag(b)].filter(Boolean).join(' · ');
  const bar = `<div class="ib-bar"><button class="ib-back" data-act="ib-r-back">${esc(S.back)}</button>`
    + `<span class="ib-path">${esc(word || bucketTag(b))}</span></div>`;
  const head = `<div class="ib-oh b-${b}"><span class="ib-ohi" aria-hidden="true">${bucketIcon(b, 18)}</span>`
    + `<span class="ib-ohw"><span class="ib-ohk"><i>${esc(kind)}</i>`
    + `<span class="ib-ohs">${esc(headStamp(f.first, f.unix))}</span></span>`
    + `<b>${esc(subjectOf(f))}</b></span></div>`;
  const cells = cellsOf(f);
  const strip = word && cells.length ? `<div class="ib-ostrip">${cells.map(cellHtml).join('')}</div>` : '';
  const say = bodyOf(f);
  const body = say.length && !f.count ? `<div class="ib-ob">${say.map((p) => `<p>${p}</p>`).join('')}</div>` : '';
  const members = f.count ? `<div class="ib-fold ib-pg b-${b}" role="list">${memberLines(m, s)}</div>` : '';
  const t = f.count ? null : tableOf(f);
  const figure = f.count ? '' : figureOf(f);
  const place = placeOf(f);
  const placeLine = place && (place.name || place.cell > 0)
    ? `<div class="ib-op">${inboxIcon('pin', 14)}<span class="ib-opn"><b>${esc(place.name || cellLabel(place.cell))}</b>`
      + `${place.cell > 0 && place.name !== cellLabel(place.cell) ? `<i>${esc(cellLabel(place.cell))}</i>` : ''}</span>`
      + `${place.cell > 0 ? goToPress(place) : ''}</div>`
    : '';
  const goes = Number(m.expiresUnix) > 0
    ? `<div class="ib-exp">${esc(S.expires({ date: isoDate(Number(m.expiresUnix)) }))}</div>` : '';
  return `<div class="ib-page" role="article" aria-label="${esc(`${kind}, ${subjectOf(f)}`)}">${bar}${head}${strip}${body}`
    + `${members}${figure ? `<div class="ib-fig">${figure}</div>` : ''}${t ? tableHtml(t) : ''}`
    + `${placeLine}${pressesHtml(m, s, { goTo: false })}${goes}</div>`;
}
